import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Alert from '../components/ui/Alert';
import EmailOtpForm from '../components/auth/EmailOtpForm';
import { AuthContext } from '../context/AuthContext';
import heroImage from '../assets/hero-image-1.png';

const styles = {
  mobileSection: {
    minHeight: '250px'
  },
  desktopSection: {
    height: '100%'
  },
  responsiveForm: {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    backgroundColor: '#ffffff',
    borderRadius: '8px'
  },
  leftColumn: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center'
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  authContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f3f4f6'
  }
};

const CandidateAuthPage = () => {
  const { setAuthenticatedUser, isAuthenticated, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Capture referral code from URL on page load (for future use)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    
    if (refCode) {
      localStorage.setItem('pendingReferralCode', refCode.toUpperCase());
      console.log(' Captured referral code for candidate:', refCode.toUpperCase());
    }
  }, [location.search]);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectPath = searchParams.get('redirect');

  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user && user.userType === 'candidate') {
      const destination = user.profileCompleted
        ? (redirectPath || '/jobs')
        : '/candidate/complete-profile';
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath, user]);

  const handleAuthSuccess = async (response) => {
    const data = response?.data || response;

    if (!data?.user || !data?.token) {
      setError('Invalid authentication response');
      setSuccess('');
      return;
    }

    // Properly wait for auth state to settle before navigation
    const result = await setAuthenticatedUser({ token: data.token, user: data.user });

    if (!result) {
      setError('Failed to establish session. Please try again.');
      setSuccess('');
      return;
    }

    setSuccess('Authentication successful!');
    setError('');

    const destination = data.user.profileCompleted
      ? (redirectPath || '/jobs')
      : '/candidate/complete-profile';

    setTimeout(() => {
      navigate(destination, { replace: true });
    }, 400);
  };

  return (
    <Container fluid className="p-0" style={styles.authContainer}>
      <div className="h-100 d-flex align-items-center justify-content-center">
        <div style={{
          ...styles.contentContainer,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <div className="d-none d-lg-flex" style={styles.leftColumn}>
            <img
              src={heroImage}
              alt="Recruitment illustration"
              className="img-fluid"
              style={{ width: '400px', height: 'auto' }}
            />
          </div>

          <div
            className={`${isMobile ? 'w-100' : ''}`}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingLeft: isMobile ? 0 : '40px',
              width: isMobile ? '100%' : 'auto',
              marginTop: isMobile ? '20px' : 0
            }}
          >
            <div
              className="p-8 mx-auto"
              style={{
                ...styles.responsiveForm,
                marginTop: isMobile ? '1rem' : 0,
                background: '#ffffff',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                padding: '2.5rem'
              }}
            >
              <h2 className="text-2xl font-bold mb-6" style={{ fontSize: '32px', color: '#000' }}>
                Candidate Login
              </h2>

              <motion.div
                key="email-auth"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <EmailOtpForm
                  onSuccess={handleAuthSuccess}
                  userType="candidate"
                  buttonText="Continue"
                />
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4"
                >
                  <Alert variant="danger">
                    {error}
                  </Alert>
                </motion.div>
              )}

              {success && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4"
                >
                  <Alert variant="success">
                    {success}
                  </Alert>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default CandidateAuthPage;
